package fyi.moko.hotpl.ui.account

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fyi.moko.hotpl.domain.model.EMOTION_LABEL_MAX_LENGTH
import fyi.moko.hotpl.domain.model.Emotion
import fyi.moko.hotpl.domain.service.AuthService
import fyi.moko.hotpl.domain.service.EmotionLabelPreference
import fyi.moko.hotpl.domain.service.UnrecoverableAppleCollisionException
import fyi.moko.hotpl.domain.service.UsageEvents
import fyi.moko.hotpl.domain.service.UsageTracker
import fyi.moko.hotpl.domain.service.UsageTrackingPreference
import fyi.moko.hotpl.domain.usecase.LinkAccountUseCase
import fyi.moko.hotpl.domain.usecase.LinkAccountUseCase.AccountSwitchIncompleteException
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class AccountUiState(
    val isAnonymous: Boolean = true,
    val email: String? = null,
    val isLinking: Boolean = false,
    val pendingSwitchDecision: PendingSwitchDecision? = null,
)

/**
 * Mirrors [LinkAccountUseCase.Outcome.NeedsSwitchDecision]: the switch already landed, but
 * [previousUid] owns [localPlaceCount] local places the screen must ask the user about before
 * [AccountViewModel.resolveSwitchChoice] can finish it.
 */
data class PendingSwitchDecision(val previousUid: String, val newUid: String, val localPlaceCount: Int)

sealed interface AccountUiEvent {
    data class ShowMessage(val message: String) : AccountUiEvent
}

/**
 * Drives the Account screen: shows whether the session is still anonymous and links
 * it to a social account via [LinkAccountUseCase]. Token acquisition is the screen's
 * job (the platform [SocialSignInLauncher]); the screen calls [completeLink] with the
 * launcher's [Result].
 */
class AccountViewModel(
    private val authService: AuthService,
    private val linkAccount: LinkAccountUseCase,
    private val emotionLabelPreference: EmotionLabelPreference,
    private val usageTrackingPreference: UsageTrackingPreference,
    private val usageTracker: UsageTracker,
) : ViewModel() {

    private val _uiState = MutableStateFlow(AccountUiState())
    val uiState: StateFlow<AccountUiState> = _uiState.asStateFlow()

    /**
     * What the "Feeling names" text fields show, keyed by [Emotion.id]. Seeded from the stored
     * names once; afterwards each keystroke lands here synchronously and is persisted as a side
     * effect, so the field never waits on the store to echo the value back.
     *
     * ── 왜 저장값이 아니라 별도 초안을 바인딩하는가 ──────────────────────────────
     * 저장소는 정규화된 값(trim, 빈 값은 삭제)만 들고 있다. 그 값을 TextField에 그대로
     * 바인딩하면 사용자가 끝에 공백을 치거나 지우는 순간 값이 되돌아와 커서가 튀고, 한글은
     * 조합 중인 자모가 깨진다([HomeViewModel.queryText]와 같은 문제). 초안은 친 그대로 두고
     * 저장만 정규화한다.
     */
    private val _emotionLabelDrafts = MutableStateFlow(emotionLabelPreference.labels.value)
    val emotionLabelDrafts: StateFlow<Map<String, String>> = _emotionLabelDrafts.asStateFlow()

    fun onEmotionLabelChange(emotion: Emotion, text: String) {
        val capped = text.take(EMOTION_LABEL_MAX_LENGTH)
        _emotionLabelDrafts.update { it + (emotion.id to capped) }
        viewModelScope.launch { emotionLabelPreference.setLabel(emotion.id, capped) }
    }

    fun resetEmotionLabel(emotion: Emotion) {
        _emotionLabelDrafts.update { it - emotion.id }
        viewModelScope.launch { emotionLabelPreference.setLabel(emotion.id, null) }
    }

    /** The "send usage data" switch ([UsageTrackingPreference]). */
    val usageTrackingEnabled: StateFlow<Boolean> = usageTrackingPreference.enabled

    /**
     * Reported *before* the preference flips so that turning it off is the last thing in the
     * log rather than an unexplained silence; the tracker reads the switch at enqueue time.
     */
    fun setUsageTrackingEnabled(value: Boolean) {
        if (value) {
            viewModelScope.launch {
                usageTrackingPreference.setEnabled(true)
                usageTracker.track(UsageEvents.USAGE_TRACKING, mapOf("enabled" to "true"))
            }
        } else {
            usageTracker.track(UsageEvents.USAGE_TRACKING, mapOf("enabled" to "false"))
            viewModelScope.launch { usageTrackingPreference.setEnabled(false) }
        }
    }

    private val _uiEvents = Channel<AccountUiEvent>(Channel.BUFFERED)
    val uiEvents = _uiEvents.receiveAsFlow()

    init {
        refresh()
    }

    fun refresh() {
        viewModelScope.launch {
            _uiState.update {
                it.copy(isAnonymous = authService.isAnonymous(), email = authService.linkedEmail())
            }
        }
    }

    /**
     * Signs out the current user (anonymous or linked). The [AuthService.currentUserId]
     * flow will emit `null`, which triggers [SyncCoordinator] to detach its Firestore
     * listeners and the JS repository to swap its localStorage partition. The next
     * [ensureSignedIn] mints a fresh anonymous session.
     */
    fun signOut() {
        viewModelScope.launch {
            authService.signOut()
            // Mint the fresh anonymous session right away — the launch-time bootstrap
            // (SyncCoordinator.startSync) only runs once per process, so without this the
            // app stays userless until restart and every link attempt dead-ends with
            // "No current user to link".
            try {
                authService.ensureSignedIn()
            } catch (e: Exception) {
                // Offline: the next launch's bootstrap (or a direct sign-in) recovers.
            }
            refresh()
            _uiEvents.send(AccountUiEvent.ShowMessage("Signed out"))
        }
    }

    /** Mark that a sign-in flow is in progress (the screen is about to launch it). */
    fun startLinking() {
        _uiState.update { it.copy(isLinking = true) }
    }

    /**
     * Completes a link attempt with the token the platform launcher produced. On a
     * credential conflict [LinkAccountUseCase] transparently switches to the existing
     * account and re-brands local data; both outcomes are reported as a message.
     */
    fun completeLink(
        provider: LinkAccountUseCase.Provider,
        credentialResult: Result<LinkAccountUseCase.SocialCredential>,
    ) {
        viewModelScope.launch {
            val credential = credentialResult.getOrElse { err ->
                _uiState.update { it.copy(isLinking = false) }
                _uiEvents.send(AccountUiEvent.ShowMessage(signInErrorMessage(err)))
                return@launch
            }

            val outcome = linkAccount(provider, credential)
            handleLinkOutcome(outcome)
        }
    }

    /**
     * The user's answer to a [PendingSwitchDecision]: keep the previous session's local places
     * (carry them into the account) or discard them (use only the account's existing data).
     */
    fun resolveSwitchChoice(keepLocalData: Boolean) {
        val pending = _uiState.value.pendingSwitchDecision ?: return
        _uiState.update { it.copy(pendingSwitchDecision = null, isLinking = true) }
        viewModelScope.launch {
            val outcome = linkAccount.resolveSwitchDecision(pending.previousUid, pending.newUid, keepLocalData)
            handleLinkOutcome(outcome)
        }
    }

    private suspend fun handleLinkOutcome(outcome: Result<LinkAccountUseCase.Outcome>) {
        outcome
            .onSuccess { result ->
                when (result) {
                    LinkAccountUseCase.Outcome.Linked, is LinkAccountUseCase.Outcome.SwitchedAccount -> {
                        _uiState.update { it.copy(isLinking = false) }
                        refresh()
                        val message = when (result) {
                            LinkAccountUseCase.Outcome.Linked -> "Account linked"
                            else -> "Signed in to your existing account"
                        }
                        _uiEvents.send(AccountUiEvent.ShowMessage(message))
                    }
                    is LinkAccountUseCase.Outcome.NeedsSwitchDecision -> {
                        // Keep isLinking true: the switch isn't finished yet, and the dialog the
                        // screen shows for pendingSwitchDecision is itself the "still working"
                        // state — showing sign-in buttons again mid-decision would be confusing.
                        _uiState.update {
                            it.copy(
                                pendingSwitchDecision = PendingSwitchDecision(
                                    previousUid = result.previousUid,
                                    newUid = result.newUid,
                                    localPlaceCount = result.localPlaceCount,
                                ),
                            )
                        }
                    }
                }
            }
            .onFailure { err ->
                _uiState.update { it.copy(isLinking = false) }
                val message = when (err) {
                    is LinkAccountUseCase.AccountSwitchIncompleteException -> {
                        "Signed in to your existing account, but your local places haven't finished moving yet. " +
                            "Please stay online and restart the app to complete the sync."
                    }
                    is UnrecoverableAppleCollisionException -> {
                        // The Apple credential's email is owned by an account under another
                        // provider, and Firebase's rotated-credential recovery couldn't land
                        // either. We must not merge accounts silently; route the user to sign
                        // in with the existing method first, then link Apple from there.
                        "This Apple ID's email is already used by another sign-in method. " +
                            "Sign in with that method first, then link Apple in Account settings."
                    }
                    else -> {
                        // Surface the underlying reason (network / provider / credential) so link
                        // failures are diagnosable in the field instead of a generic message.
                        val detail = err.message?.takeIf { it.isNotBlank() }
                        if (detail != null) "Couldn't link the account: $detail" else "Couldn't link the account"
                    }
                }
                _uiEvents.send(AccountUiEvent.ShowMessage(message))
            }
    }

    private fun signInErrorMessage(error: Throwable): String = when (error) {
        is SocialSignInCancelledException -> "Sign-in cancelled"
        is SocialSignInUnavailableException -> error.message ?: "Sign-in isn't available"
        else -> "Sign-in failed"
    }
}
